import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Wifi } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ensureNamespaceLoaded } from "../../../i18n/lazyNamespaces";
import { MAX_PLATFORM_PROBE_SOCKETS } from "../../../hooks/useWorkspaceProbeSockets";
import { presenceCardSurface } from "../../../utils/teamPresence";
import { getTeamPresenceScreenTitle } from "../../../utils/teamPresenceScreenTitle";
import TeamPresenceTabBreakdown from "../../../components/TeamPresenceTabBreakdown";
import { BusyButton } from "./shared";
import { initialsFromName } from "./usePlatformConsoleOnlinePresence";

const PlatformOnlineTabPanel = ({ presence }) => {
  const { t } = useTranslation("app");
  const { t: tLive } = useTranslation("settings");
  const theme = useTheme();

  const [livePresenceTickAt, setLivePresenceTickAt] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setLivePresenceTickAt(Date.now()), 25000);
    return () => clearInterval(id);
  }, []);

  const screenTitleForPath = useCallback((path) => getTeamPresenceScreenTitle(path, t), [t]);

  const {
    loadingDominios,
    dominiosError,
    workspaceHostList,
    truncatedTotal,
    mainSocketConnected,
    socketStats,
    hostsProbed,
    probesAllConnected,
    probeWorkspaceHosts,
    onlineItems,
    refreshList,
  } = presence;

  const onlineCardSurface = useMemo(() => presenceCardSurface(theme, "online"), [theme]);

  useEffect(() => {
    ensureNamespaceLoaded("settings");
  }, []);

  if (loadingDominios && !workspaceHostList.length && !dominiosError) {
    return (
      <Box sx={{ py: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (dominiosError) {
    return (
      <Box sx={{ py: 2 }}>
        <Typography color="error">{dominiosError}</Typography>
        <BusyButton sx={{ mt: 2 }} variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={refreshList}>
          {t("billingPage.refresh")}
        </BusyButton>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 220 }}>
          {t("platformPage.onlineIntro")}
        </Typography>
        <BusyButton
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={refreshList}
          loading={loadingDominios}
          loadingText={t("billingPage.refresh")}
        >
          {t("billingPage.refresh")}
        </BusyButton>
      </Stack>

      {!mainSocketConnected ? (
        <Alert severity="warning">{t("platformPage.onlineMainSocketDisconnected")}</Alert>
      ) : null}

      {truncatedTotal ? (
        <Alert severity="info">{t("platformPage.onlineWorkspaceCap", { max: MAX_PLATFORM_PROBE_SOCKETS })}</Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block">
        {t("platformPage.onlineMetaSocket", {
          count: onlineItems.length,
          workspaces: workspaceHostList.length,
          probes: hostsProbed.length,
          connected: socketStats.connected,
          total: socketStats.total,
        })}
      </Typography>

      {probeWorkspaceHosts.length > 0 && !probesAllConnected ? (
        <Typography variant="caption" color="text.secondary">
          {t("platformPage.onlineSocketsConnecting")}
        </Typography>
      ) : null}

      {!onlineItems.length ? (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography color="text.secondary">{t("platformPage.onlineEmpty")}</Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.5}>
          {onlineItems.map((row) => {
            const tabs = row.tabs > 0 ? row.tabs : 0;
            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={row.key}>
                <Card
                  variant="outlined"
                  sx={{
                    height: "100%",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    borderColor: onlineCardSurface.borderColor,
                    bgcolor: onlineCardSurface.bgcolor,
                    borderLeftWidth: 4,
                    borderLeftStyle: "solid",
                    borderLeftColor: onlineCardSurface.accent,
                    boxShadow: (th) =>
                      th.palette.mode === "dark"
                        ? `0 1px 4px ${alpha("#000", 0.25)}`
                        : `0 1px 2px ${alpha(th.palette.common.black, 0.06)}`,
                  }}
                >
                  <CardContent
                    sx={{
                      px: 1.75,
                      py: 1.5,
                      "&:last-child": { pb: 1.5 },
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      <Avatar
                        sx={{
                          width: 44,
                          height: 44,
                          fontSize: "0.9rem",
                          fontWeight: 700,
                          border: 2,
                          borderColor: onlineCardSurface.accent,
                          color: "text.primary",
                          bgcolor: (th) => alpha(th.palette.background.paper, 0.85),
                          flexShrink: 0,
                          mt: 0.125,
                        }}
                      >
                        {initialsFromName(row.nome)}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            noWrap
                            title={row.nome}
                            sx={{ lineHeight: 1.3 }}
                          >
                            {row.nome || "—"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            title={row.workspace_host}
                            sx={{ display: "block", mt: 0.25, lineHeight: 1.35 }}
                          >
                            {row.workspace_host || "—"}
                          </Typography>
                        </Box>
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          sx={{ columnGap: 0.75, rowGap: 0.5, alignItems: "center" }}
                        >
                          <Tooltip title={tLive("dashboardPage.live.onlinePresenceTooltip")}>
                            <Chip
                              size="small"
                              icon={<Wifi sx={{ "&&": { fontSize: 16 } }} />}
                              label={tLive("dashboardPage.live.onlineNow")}
                              color="success"
                              variant="outlined"
                              sx={{
                                cursor: "help",
                                fontWeight: 600,
                                maxWidth: "100%",
                                height: 28,
                                borderColor: onlineCardSurface.accent,
                                bgcolor: (th) => alpha(th.palette.background.paper, th.palette.mode === "dark" ? 0.2 : 0.65),
                                "& .MuiChip-label": { px: 0.65, py: 0 },
                              }}
                            />
                          </Tooltip>
                        </Stack>
                        <TeamPresenceTabBreakdown
                          show={tabs > 0}
                          socketTabs={tabs}
                          presenceDetail={row.presence_detail}
                          tLive={tLive}
                          screenTitleForPath={screenTitleForPath}
                          livePresenceNowMs={livePresenceTickAt}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Stack>
  );
};

export default PlatformOnlineTabPanel;
